/**
 * Sidebar Main Module
 *
 * Main entry point for sidebar initialization and state management.
 * Coordinates between DOM manipulation, state persistence, and Flowbite integration.
 */

import {
  enableTransitionsAfterLoad,
  getSidebarElements,
  initializeNestedMenuItems,
  toggleSidebarMobile,
  updateBottomMenuLayout,
  updateMainContentMargin,
  updateNestedMenuItems,
  updateSidebarWidthClasses,
  updateToggleItems,
} from "./dom";
import {
  collapseAllDropdowns,
  initDropdownPersistence,
  onFlowbiteReady,
  restoreDropdownStates,
} from "./dropdowns";
import {
  getSavedSidebarState,
  initializeSidebarState,
  isSidebarExpanded,
  saveSidebarState,
  setAriaExpanded,
} from "./state";
// Types imported from ./types are used for documentation, not directly referenced
import { setSidebarMode } from "./utils";

// ============================================
// Sidebar State
// ============================================

/** Shared state reference for temporary expansion tracking */
const stateRef: { isTemporaryExpansion: boolean } = {
  isTemporaryExpansion: false,
};

// ============================================
// Toggle Sidebar
// ============================================

/**
 * Toggles the sidebar between expanded and mini modes.
 *
 * Updates DOM classes, visibility, and optionally persists state.
 *
 * Args:
 *   sidebarEl: The sidebar HTMLElement.
 *   expand: True to expand, false to collapse to mini mode.
 *   saveState: If true, persists the state and updates toggle button aria.
 */
export function toggleSidebar(sidebarEl: HTMLElement, expand: boolean, saveState = false): void {
  const elements = getSidebarElements();

  // Update data attributes
  setSidebarMode(sidebarEl, expand ? "expanded" : "mini");

  // Update sidebar width
  updateSidebarWidthClasses(sidebarEl, expand);

  // Update main content margin (only when saving permanent state)
  if (saveState) {
    updateMainContentMargin(elements.mainContent, expand);
  }

  // Update toggle item visibility
  updateToggleItems(sidebarEl, expand);

  // Update nested menu item text (full vs abbreviated)
  updateNestedMenuItems(sidebarEl, expand);

  // Update bottom menu layout
  updateBottomMenuLayout(elements.bottomMenu, expand);

  // Update toggle button aria and persist state
  if (saveState && elements.toggleButton) {
    setAriaExpanded(elements.toggleButton, expand);

    // Save to localStorage
    saveSidebarState(expand);
  }

  // Clear temporary expansion flag when saving state
  if (saveState) {
    stateRef.isTemporaryExpansion = false;
  }
}

// ============================================
// Event Handlers
// ============================================

/**
 * Binds the toggle button click handler.
 */
function bindToggleButton(sidebar: HTMLElement, toggleButton: HTMLElement): void {
  toggleButton.addEventListener("click", () => {
    stateRef.isTemporaryExpansion = false; // Ensure permanent toggle
    const willExpand = !isSidebarExpanded(toggleButton);

    if (!willExpand) {
      collapseAllDropdowns(); // Collapse dropdowns when going to mini mode
    }

    toggleSidebar(sidebar, willExpand, true);

    if (willExpand) {
      restoreDropdownStates();
    }
  });
}

/**
 * Binds hover behavior for sidebar expansion.
 *
 * When flyouts are enabled (data-use-flyouts attribute), hover expansion
 * is disabled and handled by the flyout components instead.
 */
function bindHoverBehavior(sidebar: HTMLElement, toggleButton: HTMLElement | null): void {
  sidebar.addEventListener("mouseenter", () => {
    // When flyouts are enabled, don't expand the entire sidebar on hover
    const useFlyouts = sidebar.hasAttribute("data-use-flyouts");
    if (useFlyouts) return;

    if (!isSidebarExpanded(toggleButton)) {
      stateRef.isTemporaryExpansion = true; // Mark as temporary hover expansion
      collapseAllDropdowns(); // Clear template's auto-expanded dropdowns
      restoreDropdownStates(); // Restore user's saved dropdown states
      toggleSidebar(sidebar, true);
    }
  });

  sidebar.addEventListener("mouseleave", () => {
    // When flyouts are enabled, don't collapse the sidebar on mouse leave
    const useFlyouts = sidebar.hasAttribute("data-use-flyouts");
    if (useFlyouts) return;

    if (!isSidebarExpanded(toggleButton)) {
      stateRef.isTemporaryExpansion = false; // Clear temporary expansion flag
      toggleSidebar(sidebar, false);
    }
  });
}

/**
 * Binds mobile menu toggle handlers.
 */
function bindMobileMenu(
  sidebar: HTMLElement,
  elements: ReturnType<typeof getSidebarElements>,
): void {
  const toggle = () => {
    toggleSidebarMobile(sidebar, elements.backdrop, elements.mobileHamburger, elements.mobileClose);
  };

  elements.mobileToggle?.addEventListener("click", toggle);
  elements.mobileSearch?.addEventListener("click", toggle);
  elements.backdrop?.addEventListener("click", toggle);
}

// ============================================
// Initialization
// ============================================

/**
 * Initializes the sidebar module.
 *
 * Sets up:
 * - Initial sidebar state based on localStorage
 * - Nested menu item text wrapping
 * - Toggle button click handler
 * - Hover behavior (when flyouts are disabled)
 * - Mobile menu handlers
 * - Dropdown state persistence
 *
 * Should be called after DOM is ready.
 */
export function initializeSidebar(): void {
  const elements = getSidebarElements();

  // Exit early if sidebar doesn't exist
  if (!elements.sidebar) return;

  const sidebar = elements.sidebar;

  // Initialize nested menu items with text wrapping
  initializeNestedMenuItems(sidebar);

  // Initialize default sidebar state if not set
  initializeSidebarState();

  // Get saved state and apply it
  const shouldBeExpanded = getSavedSidebarState();
  toggleSidebar(sidebar, shouldBeExpanded, true);

  // Enable transitions after initial render (for both sidebar and main content)
  enableTransitionsAfterLoad([elements.mainContent, sidebar]);

  // Bind toggle button
  if (elements.toggleButton) {
    bindToggleButton(sidebar, elements.toggleButton);
  }

  // Bind hover behavior
  bindHoverBehavior(sidebar, elements.toggleButton);

  // Bind mobile menu
  bindMobileMenu(sidebar, elements);

  // Initialize dropdown persistence after Flowbite is ready
  onFlowbiteReady(() => {
    restoreDropdownStates();
    initDropdownPersistence(sidebar, stateRef);
  });
}

/**
 * Gets the current temporary expansion state.
 *
 * Used for testing and debugging.
 *
 * Returns:
 *   boolean: True if sidebar is in temporary hover expansion mode.
 */
export function isTemporaryExpansion(): boolean {
  return stateRef.isTemporaryExpansion;
}
